import {
  adjustFromTimeZoneSafely,
  adjustToTimeZoneSafely,
  extractApiErrorMessage,
  personType,
  type Shift,
  unitOfMeasureTypes,
  useGetAllUnitOfMeasuresQuery,
  useGetShiftQuery,
  useSaveShiftMutation,
} from "@ots-frontend/ots-data";
import { skipToken } from "@reduxjs/toolkit/query";
import { useContext, useMemo, useState } from "react";
import { AuthContext } from "../app/AuthProvider";
import { useOptionalOtsToast } from "../components/OtsToast";
import type { OtsUnitOfMeasureOption } from "../select/OtsUnitOfMeasureSelect";
import { type ShiftDraft, shiftDraftFromShift, type ShiftFormNames, type ShiftFormValues, toShiftPayload } from "./shift-form";

const TIME_FIELDS = ["startDttm", "endDttm", "preDttm", "postDttm"] as const;

/** Moves a draft's or the form's times between instants and the app time zone's wall clock, which the pickers show. */
const convertTimes = <T extends Partial<Record<(typeof TIME_FIELDS)[number], number>>>(value: T, convert: (time: number | undefined) => Date | undefined): T => {
  const result = { ...value };
  for (const field of TIME_FIELDS) result[field] = convert(value[field])?.getTime() as T[typeof field];
  return result;
};

export type UseShiftFormSaveOptions = {
  /** What the form starts from; one with an `id` edits that shift. Nothing while closed. */
  draft?: ShiftDraft;
  /** The region a new shift is created in, when the draft does not say. */
  regionId?: string;
  /** After the save; the shift is already merged into any loaded day of driver activity. */
  onSaved?: (shift: Shift) => void;
};

/**
 * What a shift form needs from the shift service: the stored shift for an edit, odometer units, and a save that
 * turns the form's wall-clock times back into instants. Shared by the shift page and the shift dialog.
 */
export const useShiftFormSave = ({ draft, regionId, onSaved }: UseShiftFormSaveOptions) => {
  const editing = !!draft?.id;
  // An edit saves over the stored shift, not the summary a list showed it with.
  const stored = useGetShiftQuery(draft?.id ?? skipToken);
  const units = useGetAllUnitOfMeasuresQuery(draft ? undefined : skipToken);
  const [saveShift, { isLoading: saving }] = useSaveShiftMutation();
  const [error, setError] = useState<string>();
  const toast = useOptionalOtsToast();
  // Billable minutes are an admin's to set, as in WaterGo. Without an auth provider (stories), they show.
  const auth = useContext(AuthContext);
  const showBillableTimes = !auth || auth.user?.type?.code === personType.admin.code;

  const odometerUnits = useMemo<OtsUnitOfMeasureOption[]>(() => (units.data ?? [])
    .filter((unit) => unit.type === unitOfMeasureTypes.linear.code)
    .map((unit) => ({ value: unit.name, label: unit.name, abbreviation: unit.abbr })), [units.data]);
  // An edit starts from the stored shift once it arrives; until then from the draft it was opened with.
  const start = editing && stored.data ? shiftDraftFromShift(stored.data) : draft;
  const initialValues = useMemo<ShiftFormValues>(() => start ? convertTimes({ ...start }, adjustFromTimeZoneSafely) : {}, [start]);

  const submit = async (values: ShiftFormValues, names: ShiftFormNames) => {
    if (!draft) return;
    setError(undefined);
    const payload = toShiftPayload(convertTimes(values, adjustToTimeZoneSafely), {
      stored: editing ? stored.data : undefined,
      regionId: draft.regionId ?? regionId,
      shiftTypeId: draft.shiftTypeId,
      names,
    });
    try {
      const saved = await saveShift(payload).unwrap();
      toast?.show({ title: editing ? "Shift saved" : "Shift created", tone: "success" });
      onSaved?.(saved);
    } catch (failure) {
      setError(extractApiErrorMessage(failure) ?? "The shift could not be saved.");
    }
  };

  return {
    editing,
    initialValues,
    odometerUnits,
    showBillableTimes,
    stored: stored.data,
    // The edited shift's names, for selections that are not on the region's lists.
    currentNames: editing && stored.data
      ? { personName: stored.data.personName, vehicleName: stored.data.vehicleName, trailerName: stored.data.trailerName }
      : undefined,
    loading: editing && !stored.data && !stored.isError,
    error: error ?? (editing && stored.isError ? "The shift could not be loaded." : undefined),
    saving,
    submit,
    clearError: () => setError(undefined),
  };
};
